import { Trans } from '@lingui/react/macro'
import { Button } from '@tecsinapse/cortex-react'
import React from 'react'
import { useToolbarActions } from '../hooks/use-toolbar-actions'
import { useToolbarElement } from '../hooks/use-toolbar-element'
import { MoverDialog } from './mover-dialog'
import { RenomearDialog } from './renomear-dialog'
import { ConcederAcessoDialog } from './conceder-acesso-dialog'
import { useRemover } from '../hooks/use-remover'
import { SelectedItemBiblioteca } from '@/modules/biblioteca-virtual/types/types'
import { AlterarProprietarioDialog } from './alterar-proprietario-dialog'
import { useUsuarioLogado } from '../../../core/hooks/use-usuario-logado'
import { TipoUsuario } from '../../../core/enums/tipo-usuario'
import { AccessControl } from '../../../core/utils/access-control'
import { EventGoal, EventOrigin, logEvent } from '@/core/analytics'
import { AnalyticsEvents } from '@/core/analytics/analytics-eventes-enum'

const ToolbarElement = ({
  selectedRows,
  resetSelection
}: {
  selectedRows: SelectedItemBiblioteca[]
  resetSelection: () => void
}) => {
  const {
    pastasSelecionadas,
    isSelected,
    disabledRemove,
    disabledMove,
    disabledConcederAcesso,
    disabledAlterarProprietario,
    disabledRenomear,
    handleDownload
  } = useToolbarActions(selectedRows)

  const {
    setFolderToMove,
    folderToMove,
    folderToGrant,
    setFolderToGrant,
    folderToRename,
    setFolderToRename,
    folderToAlterarProprietario,
    setFolderToAlterarProprietario
  } = useToolbarElement()

  const { usuarioLogado } = useUsuarioLogado()

  const { handleRemover } = useRemover({
    close: resetSelection
  })

  if (isSelected.length === 0) return null

  return (
    <div
      className='bg-primary-xlight border border-primary-light px-deca py-mili rounded-mili flex flex-row items-start text-white'
      data-testid='toolbar'
    >
      <Button
        variants={{ variant: 'text' }}
        onClick={() => {
          logEvent({
            name: AnalyticsEvents.REMOVER_BIBLIOTECA_VIRTUAL,
            params: {
              origin: EventOrigin.button,
              goal: EventGoal.submit
            }
          })
          handleRemover(isSelected.map(rowData => rowData.data))
        }}
        disabled={disabledRemove()}
      >
        <Trans>Excluir</Trans>
      </Button>
      <Button
        variants={{ variant: 'text' }}
        onClick={() => {
          logEvent({
            name: AnalyticsEvents.MOVER_BIBLIOTECA_VIRTUAL,
            params: {
              origin: EventOrigin.button,
              goal: EventGoal.submit
            }
          })
          setFolderToMove(isSelected.map(rowData => rowData.data))
        }}
        disabled={disabledMove()}
        data-testid='move-button'
      >
        <p>
          <Trans>Mover</Trans>
        </p>
      </Button>
      <Button
        variants={{ variant: 'text' }}
        onClick={() => {
          logEvent({
            name: AnalyticsEvents.DOWNLOAD_BIBLIOTECA_VIRTUAL,
            params: {
              origin: EventOrigin.button,
              goal: EventGoal.download
            }
          })
          handleDownload()
        }}
        data-testid='download-button'
      >
        <Trans>Download</Trans>
      </Button>
      {pastasSelecionadas.length > 0 ? (
        <>
          <AccessControl allowedUserTypes={[TipoUsuario.TECSINAPSE]}>
            <Button
              variants={{ variant: 'text' }}
              disabled={disabledAlterarProprietario()}
              onClick={() => {
                logEvent({
                  name: AnalyticsEvents.ALTERAR_PROPRIETARIO_BIBLIOTECA_VIRTUAL,
                  params: {
                    origin: EventOrigin.button,
                    goal: EventGoal.submit
                  }
                })
                setFolderToAlterarProprietario(isSelected[0].data)
              }}
              data-testid='alterar-proprietario-button'
            >
              <Trans>Alterar Proprietário</Trans>
            </Button>
          </AccessControl>
          <Button
            variants={{ variant: 'text' }}
            onClick={() => {
              logEvent({
                name: AnalyticsEvents.CONCEDER_ACESSO_BIBLIOTECA_VIRTUAL,
                params: {
                  origin: EventOrigin.button,
                  goal: EventGoal.submit
                }
              })
              setFolderToGrant(isSelected[0].data)
            }}
            disabled={disabledConcederAcesso()}
            data-testid='grant-button'
          >
            <Trans>Conceder Acesso</Trans>
          </Button>
          <Button
            variants={{ variant: 'text' }}
            onClick={() => {
              logEvent({
                name: AnalyticsEvents.RENOMEAR_BIBLIOTECA_VIRTUAL,
                params: {
                  origin: EventOrigin.button,
                  goal: EventGoal.submit
                }
              })
              setFolderToRename(isSelected[0].data)
            }}
            disabled={disabledRenomear()}
            data-testid='rename-button'
          >
            <p>Renomear</p>
          </Button>
        </>
      ) : null}
      <RenomearDialog
        open={folderToRename !== undefined}
        close={() => {
          setFolderToRename(undefined)
          resetSelection()
        }}
        pastaAtual={folderToRename}
      />
      <AlterarProprietarioDialog
        open={folderToAlterarProprietario !== undefined}
        close={() => {
          setFolderToAlterarProprietario(undefined)
          resetSelection()
        }}
        pastaAtual={folderToAlterarProprietario}
      />
      <MoverDialog
        open={folderToMove.length > 0}
        close={() => {
          setFolderToMove([])
          resetSelection()
        }}
        items={folderToMove}
      />
      <ConcederAcessoDialog
        open={folderToGrant !== undefined}
        close={() => {
          setFolderToGrant(undefined)
          resetSelection()
        }}
        pastaAtual={folderToGrant}
      />
    </div>
  )
}

export default ToolbarElement
